// ========================================
// TYPE-SPECIFIC FIELD COMPONENTS V3
// Reusable field components for different order types
// ========================================

import React, { useState, useEffect } from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  Autocomplete
} from '@mui/material';
import {
  MaterialData,
  HeatTreatmentData,
  CoatingData,
  ToolData,
  CarbideData
} from '../../types/unifiedOrderTypes';

// ========================================
// MATERIAL FIELDS COMPONENT
// ========================================

interface MaterialFieldsProps {
  data: MaterialData;
  onChange: (data: MaterialData) => void;
  disabled?: boolean;
}

export const MaterialFields: React.FC<MaterialFieldsProps> = ({ data, onChange, disabled = false }) => {
  // Auto-fill suggestions state
  const [previousSteelQualities, setPreviousSteelQualities] = useState<string[]>([]);
  const [previousCrossSections, setPreviousCrossSections] = useState<string[]>([]);
  
  // Load previous values from localStorage
  useEffect(() => {
    const savedSteelQualities = localStorage.getItem('wzf_previous_steel_qualities');
    const savedCrossSections = localStorage.getItem('wzf_previous_cross_sections');
    
    if (savedSteelQualities) {
      setPreviousSteelQualities(JSON.parse(savedSteelQualities));
    }
    
    if (savedCrossSections) {
      setPreviousCrossSections(JSON.parse(savedCrossSections));
    }
  }, []);

  const updateField = (field: keyof MaterialData, value: any) => {
    onChange({ ...data, [field]: value });
    
    // Save to localStorage for auto-fill suggestions
    if (field === 'steel_quality' && value && !previousSteelQualities.includes(value)) {
      const updated = [...previousSteelQualities, value];
      setPreviousSteelQualities(updated);
      localStorage.setItem('wzf_previous_steel_qualities', JSON.stringify(updated));
    } else if (field === 'cross_section' && value && !previousCrossSections.includes(value)) {
      const updated = [...previousCrossSections, value];
      setPreviousCrossSections(updated);
      localStorage.setItem('wzf_previous_cross_sections', JSON.stringify(updated));
    }
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Materialspezifikationen {/* Material Specifications */}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ 
            flex: '1 1 300px', 
            minWidth: '250px',
            '@media (min-width: 900px) and (max-width: 1200px) and (min-aspect-ratio: 1.6/1) and (max-aspect-ratio: 1.7/1)': {
              flex: '1 1 280px',
              minWidth: '220px'
            }
          }}>
            <FormControl fullWidth>
              <InputLabel>Form {/* Form */}</InputLabel>
              <Select
                value={data.form || ''}
                onChange={(e) => updateField('form', e.target.value)}
                disabled={disabled}
                label="Form" // Form
              >
                <MenuItem value="Round">Rund {/* Round */}</MenuItem>
                <MenuItem value="Square">Quadrat {/* Square */}</MenuItem>
                <MenuItem value="Flat">Flach {/* Flat */}</MenuItem>
                <MenuItem value="Hexagon">Sechskant {/* Hexagon */}</MenuItem>
                <MenuItem value="Rectangle">Rechteck {/* Rectangle */}</MenuItem>
                <MenuItem value="Other">Andere {/* Other */}</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ 
            flex: '1 1 300px', 
            minWidth: '250px',
            '@media (min-width: 900px) and (max-width: 1200px) and (min-aspect-ratio: 1.6/1) and (max-aspect-ratio: 1.7/1)': {
              flex: '1 1 280px',
              minWidth: '220px'
            }
          }}>
            <Autocomplete
              freeSolo
              options={previousCrossSections}
              value={data.cross_section || ''}
              onInputChange={(_, value) => updateField('cross_section', value || '')}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Querschnitt" // Cross Section
                  placeholder="z.B. 10x10mm, Ø15mm" // e.g., 10x10mm, Ø15mm
                  helperText="Abmessungen - Vorschläge basierend auf früheren Eingaben" // Dimensions - suggestions based on previous entries
                />
              )}
            />
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ 
            flex: '1 1 300px', 
            minWidth: '250px',
            '@media (min-width: 900px) and (max-width: 1200px) and (min-aspect-ratio: 1.6/1) and (max-aspect-ratio: 1.7/1)': {
              flex: '1 1 280px',
              minWidth: '220px'
            }
          }}>
            <TextField
              fullWidth
              label="Länge (mm)" // Length (mm)
              type="number"
              value={data.length_mm || ''}
              onChange={(e) => updateField('length_mm', e.target.value ? Number(e.target.value) : undefined)}
              disabled={disabled}
              helperText="Länge in Millimetern" // Length in millimeters
            />
          </Box>
          
          <Box sx={{ 
            flex: '1 1 300px', 
            minWidth: '250px',
            '@media (min-width: 900px) and (max-width: 1200px) and (min-aspect-ratio: 1.6/1) and (max-aspect-ratio: 1.7/1)': {
              flex: '1 1 280px',
              minWidth: '220px'
            }
          }}>
            <Autocomplete
              freeSolo
              options={previousSteelQualities}
              value={data.steel_quality || ''}
              onInputChange={(_, value) => updateField('steel_quality', value || '')}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Stahlqualität" // Steel Quality
                  placeholder="z.B. 1.2379, 1.3343" // e.g., 1.2379, 1.3343
                  helperText="Materialgrad - Vorschläge basierend auf früheren Eingaben" // Material grade - suggestions based on previous entries
                />
              )}
            />
          </Box>
        </Box>
        
        <TextField
          fullWidth
          label="Interne Bestellreferenz" // Internal Order Reference
          value={data.internal_order_reference || ''}
          onChange={(e) => updateField('internal_order_reference', e.target.value)}
          disabled={disabled}
          placeholder="Interne Verfolgungs-Referenz" // Internal tracking reference
        />
      </Box>
    </>
  );
};

// ========================================
// HEAT TREATMENT FIELDS COMPONENT
// ========================================

interface HeatTreatmentFieldsProps {
  data: HeatTreatmentData;
  onChange: (data: HeatTreatmentData) => void;
  disabled?: boolean;
}

export const HeatTreatmentFields: React.FC<HeatTreatmentFieldsProps> = ({ data, onChange, disabled = false }) => {
  // Auto-fill suggestions state
  const [previousTreatmentTypes, setPreviousTreatmentTypes] = useState<string[]>([]);
  const [previousHrcRanges, setPreviousHrcRanges] = useState<string[]>([]);
  const [previousSteelQualities, setPreviousSteelQualities] = useState<string[]>([]);
  
  // Load previous values from localStorage
  useEffect(() => {
    const savedTreatmentTypes = localStorage.getItem('wzf_previous_treatment_types');
    const savedHrcRanges = localStorage.getItem('wzf_previous_hrc_ranges');
    const savedSteelQualities = localStorage.getItem('wzf_previous_steel_qualities');
    
    if (savedTreatmentTypes) {
      setPreviousTreatmentTypes(JSON.parse(savedTreatmentTypes));
    }
    
    if (savedHrcRanges) {
      setPreviousHrcRanges(JSON.parse(savedHrcRanges));
    }
    
    if (savedSteelQualities) {
      setPreviousSteelQualities(JSON.parse(savedSteelQualities));
    }
  }, []);

  const updateField = (field: keyof HeatTreatmentData, value: any) => {
    onChange({ ...data, [field]: value });
    
    // Save to localStorage for auto-fill suggestions
    if (field === 'treatment_type' && value && !previousTreatmentTypes.includes(value)) {
      const updated = [...previousTreatmentTypes, value];
      setPreviousTreatmentTypes(updated);
      localStorage.setItem('wzf_previous_treatment_types', JSON.stringify(updated));
    } else if (field === 'hrc_range' && value && !previousHrcRanges.includes(value)) {
      const updated = [...previousHrcRanges, value];
      setPreviousHrcRanges(updated);
      localStorage.setItem('wzf_previous_hrc_ranges', JSON.stringify(updated));
    } else if (field === 'steel_quality' && value && !previousSteelQualities.includes(value)) {
      const updated = [...previousSteelQualities, value];
      setPreviousSteelQualities(updated);
      localStorage.setItem('wzf_previous_steel_qualities', JSON.stringify(updated));
    }
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Wärmebehandlungsspezifikationen {/* Heat Treatment Specifications */}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ 
            flex: '1 1 300px', 
            minWidth: '250px',
            '@media (min-width: 900px) and (max-width: 1200px) and (min-aspect-ratio: 1.6/1) and (max-aspect-ratio: 1.7/1)': {
              flex: '1 1 280px',
              minWidth: '220px'
            }
          }}>
            <FormControl fullWidth>
              <InputLabel>Behandlungstyp {/* Treatment Type */}</InputLabel>
              <Select
                value={data.treatment_type || ''}
                onChange={(e) => updateField('treatment_type', e.target.value)}
                disabled={disabled}
                label="Behandlungstyp" // Treatment Type
              >
                <MenuItem value="Vacuum-Hardening">Vacuum-Härten {/* Vacuum-Hardening */}</MenuItem>
                <MenuItem value="Tempering">Anlassen {/* Tempering */}</MenuItem>
                <MenuItem value="Annealing">Glühen {/* Annealing */}</MenuItem>
                <MenuItem value="Normalizing">Normalglühen {/* Normalizing */}</MenuItem>
                <MenuItem value="Quenching">Abschrecken {/* Quenching */}</MenuItem>
                <MenuItem value="Case Hardening">Einsatzhärten {/* Case Hardening */}</MenuItem>
                <MenuItem value="Nitriding">Nitrieren {/* Nitriding */}</MenuItem>
                <MenuItem value="Other">Andere {/* Other */}</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ 
            flex: '1 1 300px', 
            minWidth: '250px',
            '@media (min-width: 900px) and (max-width: 1200px) and (min-aspect-ratio: 1.6/1) and (max-aspect-ratio: 1.7/1)': {
              flex: '1 1 280px',
              minWidth: '220px'
            }
          }}>
            <Autocomplete
              freeSolo
              options={previousHrcRanges}
              value={data.hrc_range || ''}
              onInputChange={(_, value) => updateField('hrc_range', value || '')}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="HRC-Bereich" // HRC Range
                  placeholder="z.B. 58-62 HRC" // e.g., 58-62 HRC
                  helperText="Härtebereich - Vorschläge basierend auf früheren Eingaben" // Hardness range - suggestions based on previous entries
                />
              )}
            />
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ 
            flex: '1 1 300px', 
            minWidth: '250px',
            '@media (min-width: 900px) and (max-width: 1200px) and (min-aspect-ratio: 1.6/1) and (max-aspect-ratio: 1.7/1)': {
              flex: '1 1 280px',
              minWidth: '220px'
            }
          }}>
            <Autocomplete
              freeSolo
              options={previousSteelQualities}
              value={data.steel_quality || ''}
              onInputChange={(_, value) => updateField('steel_quality', value || '')}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Stahlqualität" // Steel Quality
                  placeholder="z.B. 1.2379, 1.3343" // e.g., 1.2379, 1.3343
                  helperText="Materialspezifikation - Vorschläge basierend auf früheren Eingaben" // Material specification - suggestions based on previous entries
                />
              )}
            />
          </Box>
          
          <Box sx={{ 
            flex: '1 1 300px', 
            minWidth: '250px',
            '@media (min-width: 900px) and (max-width: 1200px) and (min-aspect-ratio: 1.6/1) and (max-aspect-ratio: 1.7/1)': {
              flex: '1 1 280px',
              minWidth: '220px'
            }
          }}>
            <TextField
              fullWidth
              label="Gewicht (kg)" // Weight (kg)
              type="number"
              value={data.weight_kg || ''}
              onChange={(e) => updateField('weight_kg', e.target.value ? Number(e.target.value) : undefined)}
              disabled={disabled}
              helperText="Gewicht in Kilogramm" // Weight in kilograms
              inputProps={{ step: "0.01", min: "0" }}
            />
          </Box>
        </Box>
        
        <TextField
          fullWidth
          label="Interne Bestellreferenz" // Internal Order Reference
          value={data.internal_order_reference || ''}
          onChange={(e) => updateField('internal_order_reference', e.target.value)}
          disabled={disabled}
          placeholder="Interne Verfolgungs-Referenz" // Internal tracking reference
        />

        <TextField
          fullWidth
          label="Zusätzliche Informationen" // Additional Information
          value={data.additional_info || ''}
          onChange={(e) => updateField('additional_info', e.target.value)}
          disabled={disabled}
          multiline
          rows={3}
          placeholder="Zusätzliche Notizen oder spezielle Anforderungen" // Additional notes or special requirements
        />
      </Box>
    </>
  );
};

// ========================================
// COATING FIELDS COMPONENT
// ========================================

interface CoatingFieldsProps {
  data: CoatingData;
  onChange: (data: CoatingData) => void;
  disabled?: boolean;
}

export const CoatingFields: React.FC<CoatingFieldsProps> = ({ data, onChange, disabled = false }) => {
  // Auto-fill suggestions state
  const [previousCoatingSpecs, setPreviousCoatingSpecs] = useState<string[]>([]);
  const [previousSteelQualities, setPreviousSteelQualities] = useState<string[]>([]);
  
  // Load previous values from localStorage
  useEffect(() => {
    const savedCoatingSpecs = localStorage.getItem('wzf_previous_coating_specs');
    const savedSteelQualities = localStorage.getItem('wzf_previous_steel_qualities');
    
    if (savedCoatingSpecs) {
      setPreviousCoatingSpecs(JSON.parse(savedCoatingSpecs));
    }
    
    if (savedSteelQualities) {
      setPreviousSteelQualities(JSON.parse(savedSteelQualities));
    }
  }, []);

  const updateField = (field: keyof CoatingData, value: any) => {
    onChange({ ...data, [field]: value });
    
    // Save to localStorage for auto-fill suggestions
    if (field === 'coating_spec' && value && !previousCoatingSpecs.includes(value)) {
      const updated = [...previousCoatingSpecs, value];
      setPreviousCoatingSpecs(updated);
      localStorage.setItem('wzf_previous_coating_specs', JSON.stringify(updated));
    } else if (field === 'steel_quality' && value && !previousSteelQualities.includes(value)) {
      const updated = [...previousSteelQualities, value];
      setPreviousSteelQualities(updated);
      localStorage.setItem('wzf_previous_steel_qualities', JSON.stringify(updated));
    }
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Beschichtungsspezifikationen {/* Coating Specifications */}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ 
            flex: '1 1 300px', 
            minWidth: '250px',
            '@media (min-width: 900px) and (max-width: 1200px) and (min-aspect-ratio: 1.6/1) and (max-aspect-ratio: 1.7/1)': {
              flex: '1 1 280px',
              minWidth: '220px'
            }
          }}>
            <FormControl fullWidth>
              <InputLabel>Behandlungstyp {/* Treatment Type */}</InputLabel>
              <Select
                value={data.treatment_type || ''}
                onChange={(e) => updateField('treatment_type', e.target.value)}
                disabled={disabled}
                label="Behandlungstyp" // Treatment Type
              >
                <MenuItem value="PVD">PVD</MenuItem>
                <MenuItem value="CVD">CVD</MenuItem>
                <MenuItem value="DLC">DLC (Diamant-ähnlicher Kohlenstoff) {/* DLC (Diamond-Like Carbon) */}</MenuItem>
                <MenuItem value="TiN">TiN (Titannitrid) {/* TiN (Titanium Nitride) */}</MenuItem>
                <MenuItem value="TiCN">TiCN (Titan-Karbo-Nitrid) {/* TiCN (Titanium Carbo-Nitride) */}</MenuItem>
                <MenuItem value="TiAlN">TiAlN (Titan-Aluminium-Nitrid) {/* TiAlN (Titanium Aluminum Nitride) */}</MenuItem>
                <MenuItem value="CrN">CrN (Chromnitrid) {/* CrN (Chromium Nitride) */}</MenuItem>
                <MenuItem value="AlCrN">AlCrN (Aluminium-Chrom-Nitrid) {/* AlCrN (Aluminum Chromium Nitride) */}</MenuItem>
                <MenuItem value="Other">Andere {/* Other */}</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ 
            flex: '1 1 300px', 
            minWidth: '250px',
            '@media (min-width: 900px) and (max-width: 1200px) and (min-aspect-ratio: 1.6/1) and (max-aspect-ratio: 1.7/1)': {
              flex: '1 1 280px',
              minWidth: '220px'
            }
          }}>
            <Autocomplete
              freeSolo
              options={previousCoatingSpecs}
              value={data.coating_spec || ''}
              onInputChange={(_, value) => updateField('coating_spec', value || '')}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Beschichtungsspezifikation" // Coating Specification
                  placeholder="Detaillierte Beschichtungsspezifikation" // Detailed coating specification
                  helperText="Pflichtfeld - Vorschläge basierend auf früheren Eingaben" // Required field - suggestions based on previous entries
                  required
                />
              )}
            />
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ 
            flex: '1 1 300px', 
            minWidth: '250px',
            '@media (min-width: 900px) and (max-width: 1200px) and (min-aspect-ratio: 1.6/1) and (max-aspect-ratio: 1.7/1)': {
              flex: '1 1 280px',
              minWidth: '220px'
            }
          }}>
            <Autocomplete
              freeSolo
              options={previousSteelQualities}
              value={data.steel_quality || ''}
              onInputChange={(_, value) => updateField('steel_quality', value || '')}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Stahlqualität" // Steel Quality
                  placeholder="z.B. 1.2379, 1.3343" // e.g., 1.2379, 1.3343
                  helperText="Pflichtfeld - Vorschläge basierend auf früheren Eingaben" // Required field - suggestions based on previous entries
                  required
                />
              )}
            />
          </Box>
          
          <Box sx={{ 
            flex: '1 1 300px', 
            minWidth: '250px',
            '@media (min-width: 900px) and (max-width: 1200px) and (min-aspect-ratio: 1.6/1) and (max-aspect-ratio: 1.7/1)': {
              flex: '1 1 280px',
              minWidth: '220px'
            }
          }}>
            <TextField
              fullWidth
              label="Gewicht (kg)" // Weight (kg)
              type="number"
              value={data.weight_kg || ''}
              onChange={(e) => updateField('weight_kg', e.target.value ? Number(e.target.value) : undefined)}
              disabled={disabled}
              helperText="Gewichtsspezifikation" // Weight specification
              inputProps={{ step: "0.01", min: "0" }}
            />
          </Box>
        </Box>
        
        <TextField
          fullWidth
          label="Interne Bestellreferenz" // Internal Order Reference
          value={data.internal_order_reference || ''}
          onChange={(e) => updateField('internal_order_reference', e.target.value)}
          disabled={disabled}
          placeholder="Interne Verfolgungs-Referenz" // Internal tracking reference
        />
        
        <TextField
          fullWidth
          label="Zusätzliche Informationen" // Additional Information
          value={data.additional_info || ''}
          onChange={(e) => updateField('additional_info', e.target.value)}
          disabled={disabled}
          multiline
          rows={3}
          placeholder="Zusätzliche Notizen oder spezielle Anforderungen" // Additional notes or special requirements
        />
      </Box>
    </>
  );
};

// ========================================
// TOOL FIELDS COMPONENT
// Tool-specific form fields for tool orders
// ========================================

interface ToolFieldsProps {
  data: ToolData;
  onChange: (data: ToolData) => void;
  disabled?: boolean;
}

export const ToolFields: React.FC<ToolFieldsProps> = ({ data, onChange, disabled = false }) => {
  // Store previously used values for autocomplete
  const [previousToolTypes, setPreviousToolTypes] = useState<string[]>([]);
  const [previousManufacturers, setPreviousManufacturers] = useState<string[]>([]);

  // Load previous values from localStorage for autocomplete
  useEffect(() => {
    const savedToolTypes = localStorage.getItem('wzf_previous_tool_types');
    const savedManufacturers = localStorage.getItem('wzf_previous_manufacturers');
    
    if (savedToolTypes) {
      setPreviousToolTypes(JSON.parse(savedToolTypes));
    }
    
    if (savedManufacturers) {
      setPreviousManufacturers(JSON.parse(savedManufacturers));
    }
  }, []);

  const updateField = (field: keyof ToolData, value: string) => {
    const updatedData = { ...data, [field]: value };
    onChange(updatedData);

    // Save new values for future autocomplete
    if (field === 'tool_type' && value && !previousToolTypes.includes(value)) {
      const updatedTypes = [...previousToolTypes, value];
      setPreviousToolTypes(updatedTypes);
      localStorage.setItem('wzf_previous_tool_types', JSON.stringify(updatedTypes));
    }
    
    if (field === 'manufacturer' && value && !previousManufacturers.includes(value)) {
      const updatedManufacturers = [...previousManufacturers, value];
      setPreviousManufacturers(updatedManufacturers);
      localStorage.setItem('wzf_previous_manufacturers', JSON.stringify(updatedManufacturers));
    }
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Werkzeugspezifikationen {/* Tool Specifications */}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Primary Tool Information */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ 
            flex: '1 1 300px', 
            minWidth: '250px',
            '@media (min-width: 900px) and (max-width: 1200px) and (min-aspect-ratio: 1.6/1) and (max-aspect-ratio: 1.7/1)': {
              flex: '1 1 280px',
              minWidth: '220px'
            }
          }}>
            <TextField
              fullWidth
              required
              label="Hersteller-Artikelnummer" // Manufacturer Article Number
              value={data.manufacturer_article_number || ''}
              onChange={(e) => updateField('manufacturer_article_number', e.target.value)}
              disabled={disabled}
              placeholder="z.B. RT52, SC.123456" // e.g., RT52, SC.123456
              helperText="Primärer Identifikator - wie auf der Werkzeugverpackung gedruckt" // Primary identifier - as printed on tool box
            />
          </Box>
          
          <Box sx={{ 
            flex: '1 1 300px', 
            minWidth: '250px',
            '@media (min-width: 900px) and (max-width: 1200px) and (min-aspect-ratio: 1.6/1) and (max-aspect-ratio: 1.7/1)': {
              flex: '1 1 280px',
              minWidth: '220px'
            }
          }}>
            <Autocomplete
              freeSolo
              options={previousToolTypes}
              value={data.tool_type || ''}
              onInputChange={(_, value) => updateField('tool_type', value || '')}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Werkzeugtyp" // Tool Type
                  placeholder="z.B. Fräser, Bohrer, Schleifscheibe" // e.g., Milling Cutter, Drill, Grinding Disk
                  helperText="Art des Werkzeugs" // Type of tool
                />
              )}
            />
          </Box>
        </Box>
        
        {/* Manufacturer and Description */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ 
            flex: '1 1 300px', 
            minWidth: '250px',
            '@media (min-width: 900px) and (max-width: 1200px) and (min-aspect-ratio: 1.6/1) and (max-aspect-ratio: 1.7/1)': {
              flex: '1 1 280px',
              minWidth: '220px'
            }
          }}>
            <Autocomplete
              freeSolo
              options={previousManufacturers}
              value={data.manufacturer || ''}
              onInputChange={(_, value) => updateField('manufacturer', value || '')}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Hersteller" // Manufacturer
                  placeholder="z.B. ATORN, Sandvik, Kennametal" // e.g., ATORN, Sandvik, Kennametal
                  helperText="Werkzeughersteller" // Tool manufacturer
                />
              )}
            />
          </Box>
        </Box>
        
        {/* Additional Information */}
        <TextField
          fullWidth
          required
          label="Zusätzliche Informationen" // Additional Information
          value={data.additional_info || ''}
          onChange={(e) => updateField('additional_info', e.target.value)}
          disabled={disabled}
          multiline
          rows={3}
          placeholder="z.B. ATORN SC Torusfräser, Standard-Durchmesser 10.0 x 22 x 75 mm r0.5 T=4 RT52" // e.g., ATORN SC torus milling cutter, standard diameter 10.0 x 22 x 75 mm r0.5 T=4 RT52
          helperText="Vollständige Werkzeugbeschreibung - von der Werkzeugverpackung oder Katalog kopieren" // Complete tool description - copy from tool box or catalog
        />
      </Box>
    </>
  );
};

// ========================================
// CARBIDE FIELDS COMPONENT
// ========================================

interface CarbideFieldsProps {
  data: CarbideData;
  onChange: (data: CarbideData) => void;
  disabled?: boolean;
}

export const CarbideFields: React.FC<CarbideFieldsProps> = ({ data, onChange, disabled = false }) => {
  // Auto-fill suggestions state
  const [previousMaterialSpecs, setPreviousMaterialSpecs] = useState<string[]>([]);
  
  // Load previous values from localStorage
  useEffect(() => {
    const savedMaterialSpecs = localStorage.getItem('wzf_previous_carbide_material_specs');
    
    if (savedMaterialSpecs) {
      setPreviousMaterialSpecs(JSON.parse(savedMaterialSpecs));
    }
  }, []);

  const updateField = (field: keyof CarbideData, value: any) => {
    onChange({ ...data, [field]: value });
    
    // Save to localStorage for auto-fill suggestions
    if (field === 'material_spec' && value && !previousMaterialSpecs.includes(value)) {
      const updated = [...previousMaterialSpecs, value];
      setPreviousMaterialSpecs(updated);
      localStorage.setItem('wzf_previous_carbide_material_specs', JSON.stringify(updated));
    }
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Carbide Spezifikationen {/* Carbide Specifications */}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Autocomplete
          freeSolo
          options={previousMaterialSpecs}
          value={data.material_spec || ''}
          onInputChange={(_, value) => updateField('material_spec', value || '')}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Materialspezifikation"
              multiline
              rows={3}
              placeholder="z.B. Carbide Grade K10, P20, etc. mit detaillierter Spezifikation"
              helperText="Erforderlich - Vollständige Materialspezifikation für das Carbide-Teil - Vorschläge basierend auf früheren Eingaben"
              required
            />
          )}
        />
        
        {data.dimensional_drawing_path && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Maßzeichnung hochgeladen: {data.dimensional_drawing_path}
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};
